import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { publicApi, publicUrl } from "@/lib/api";
import { ArthaLogo, Sunflower } from "@/components/ArthaLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ShieldCheck, ArrowUpRight, Copy, Check as CheckIcon } from "lucide-react";
import { toast } from "sonner";

const flagFor = (code) => ({ IN: "🇮🇳", US: "🇺🇸", GB: "🇬🇧", AE: "🇦🇪", SG: "🇸🇬", EE: "🇪🇪", CH: "🇨🇭" }[code] || "");

const PublicCompany = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    publicApi.company(slug).then(setData).catch(() => setNotFound(true));
  }, [slug]);

  const copyLink = async () => {
    const url = data?.public_url || publicUrl(slug);
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Public profile link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-surface-app text-ink-body flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <Sunflower size={60} />
          <h1 className="h-display text-5xl mt-6">Not found.</h1>
          <p className="mt-3 text-sm text-ink-muted">
            This company profile is private or doesn't exist. If you believe this is an error, contact the company directly.
          </p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 amber-link text-sm" data-testid="public-home-link">
            Visit Artha <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-surface-app text-ink-body flex items-center justify-center text-sm text-ink-muted">
        Loading…
      </div>
    );
  }

  const { compliance, captable_summary, ecosystem_products } = data;
  const healthColor = compliance.health_score >= 80 ? "text-artha-amber" : compliance.health_score >= 50 ? "text-[#D98F1C]" : "text-[#C94F4F]";

  return (
    <div className="min-h-screen bg-surface-app text-ink-body" data-testid="public-company-page">
      {/* Top nav */}
      <header className="border-b border-border-subtle">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="public-nav-logo">
            <ArthaLogo size={24} />
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-xs font-mono-accent uppercase tracking-[0.22em] text-ink-muted">
              Verified by Artha
            </span>
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-16">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8">
            <div className="flex items-center gap-2 mb-6 fade-up">
              <ShieldCheck size={14} className="text-artha-amber" />
              <span className="text-xs font-mono-accent uppercase tracking-[0.28em] text-artha-amber">
                Artha Verified
              </span>
              {data.verified_since && (
                <span className="text-xs font-mono-accent uppercase tracking-[0.22em] text-ink-muted">
                  · Since {new Date(data.verified_since).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
                </span>
              )}
            </div>
            <h1 className="h-display text-5xl md:text-7xl fade-up delay-1">
              {data.company_name}
            </h1>
            <div className="mt-6 flex items-center gap-3 flex-wrap fade-up delay-2">
              <span className="text-2xl">{flagFor(data.country)}</span>
              <span className="text-sm text-ink-muted">{data.country_name}</span>
              <span className="text-ink-muted">·</span>
              <span className="text-sm font-mono-accent uppercase tracking-[0.22em] text-ink-muted">
                {data.entity_type_name}
              </span>
              {data.registration_number && (
                <>
                  <span className="text-ink-muted">·</span>
                  <span className="text-xs font-mono-accent text-ink-muted">{data.registration_number}</span>
                </>
              )}
            </div>
            {data.business_description && (
              <p className="mt-8 text-lg text-ink-muted max-w-2xl leading-relaxed fade-up delay-3">
                {data.business_description}
              </p>
            )}
            <div className="mt-10 flex gap-3 fade-up delay-4">
              <button onClick={copyLink} data-testid="public-copy-link"
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-border-subtle hover:border-artha-amber hover:text-artha-amber text-sm transition-colors">
                {copied ? <><CheckIcon size={14} /> Copied</> : <><Copy size={14} /> Copy profile link</>}
              </button>
              <Link to="/signup" data-testid="public-cta-signup"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-artha-amber text-artha-ink text-sm hover:bg-artha-cream transition-colors">
                Incorporate your own <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-4 hidden lg:flex items-start justify-end">
            <div className="relative">
              <div className="sunflower-spin"><Sunflower size={200} /></div>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics strip */}
      <section className="border-y border-border-subtle">
        <div className="max-w-[1200px] mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-border-subtle">
          <Metric label="Compliance health" value={compliance.health_score} suffix="/100" highlight color={healthColor} />
          <Metric label="Shareholders" value={captable_summary.shareholder_count} sub={`${captable_summary.founder_count} founder${captable_summary.founder_count === 1 ? "" : "s"}`} />
          <Metric label="Shares outstanding" value={captable_summary.total_shares.toLocaleString()} />
          <Metric label="Filings on file" value={compliance.completed_tasks} sub={`${compliance.total_tasks - compliance.completed_tasks} active`} />
        </div>
      </section>

      {/* Body grid */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-16 grid grid-cols-12 gap-8">
        {/* Share class breakdown */}
        <div className="col-span-12 lg:col-span-7">
          <div className="text-xs font-mono-accent uppercase tracking-[0.28em] text-ink-muted mb-4">
            Capitalisation
          </div>
          <h2 className="h-display text-3xl md:text-4xl">
            <span className="italic text-artha-amber">{captable_summary.total_shares.toLocaleString()}</span> shares,
            distributed across <span className="italic">{Object.keys(captable_summary.share_class_breakdown).length || 1}</span> share class{Object.keys(captable_summary.share_class_breakdown).length === 1 ? "" : "es"}.
          </h2>
          <p className="text-sm text-ink-muted mt-4 max-w-xl">
            Shareholder identities are kept private; only aggregate ownership is shown on the public profile.
          </p>
          <div className="mt-10 space-y-5">
            {Object.entries(captable_summary.share_class_breakdown).map(([cls, pct], i) => (
              <div key={cls} data-testid={`public-class-${cls}`}>
                <div className="flex justify-between text-xs font-mono-accent uppercase tracking-[0.22em] text-ink-muted mb-2">
                  <span>{cls}</span>
                  <span className="text-artha-amber">{pct}%</span>
                </div>
                <div className="h-2 bg-border-subtle overflow-hidden">
                  <div className="h-full"
                    style={{
                      width: `${pct}%`,
                      background: `hsl(${(i * 47) % 360}deg 70% 50%)`,
                    }} />
                </div>
              </div>
            ))}
            {Object.keys(captable_summary.share_class_breakdown).length === 0 && (
              <div className="text-sm text-ink-muted italic">No cap table on file.</div>
            )}
          </div>
        </div>

        {/* Ecosystem + compliance detail */}
        <div className="col-span-12 lg:col-span-5 space-y-10">
          <div>
            <div className="text-xs font-mono-accent uppercase tracking-[0.28em] text-ink-muted mb-4">
              Mahakali stack
            </div>
            {ecosystem_products.length === 0 ? (
              <div className="text-sm text-ink-muted italic">No ecosystem products activated.</div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {ecosystem_products.map((p) => (
                  <div key={p.id} className="flex items-center gap-4 border border-border-subtle p-4" data-testid={`public-eco-${p.id}`}>
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">{p.name}</div>
                      <div className="text-[10px] font-mono-accent uppercase tracking-[0.22em] text-ink-muted">{p.tagline}</div>
                    </div>
                    <span className="text-[10px] font-mono-accent uppercase tracking-[0.22em] bg-artha-amber/20 text-artha-amber px-2 py-1">Active</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs font-mono-accent uppercase tracking-[0.28em] text-ink-muted mb-4">
              Regulatory standing
            </div>
            <div className="border border-border-subtle divide-y divide-border-subtle">
              <PublicRow k="Completed filings" v={compliance.completed_tasks} />
              <PublicRow k="Active obligations" v={compliance.total_tasks - compliance.completed_tasks} />
              <PublicRow k="Overdue" v={compliance.overdue_tasks} danger={compliance.overdue_tasks > 0} />
              <PublicRow k="Due in 30 days" v={compliance.upcoming_tasks} />
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="border-t border-border-subtle">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-16 flex flex-col md:flex-row justify-between gap-6 items-center">
          <div className="flex items-center gap-3">
            <ShieldCheck size={16} className="text-artha-amber" />
            <div>
              <div className="text-sm">This profile is verified by Artha.</div>
              <div className="text-xs text-ink-muted">Data is sourced from the company's live incorporation + compliance records.</div>
            </div>
          </div>
          <Link to="/" className="text-sm amber-link" data-testid="public-footer-link">
            Incorporate your company on Artha →
          </Link>
        </div>
      </section>
    </div>
  );
};

const Metric = ({ label, value, sub, suffix, highlight, color }) => (
  <div className="p-6 md:p-8">
    <div className="text-xs font-mono-accent uppercase tracking-[0.22em] text-ink-muted">{label}</div>
    <div className={`h-display mt-3 ${highlight ? "text-5xl md:text-6xl" : "text-4xl"} ${color || "text-ink-body"}`}>
      {value}
      {suffix && <span className="text-ink-muted text-base ml-1">{suffix}</span>}
    </div>
    {sub && <div className="text-xs text-ink-muted mt-2 font-mono-accent uppercase tracking-[0.22em]">{sub}</div>}
  </div>
);

const PublicRow = ({ k, v, danger }) => (
  <div className="flex justify-between px-4 py-3">
    <span className="text-xs font-mono-accent uppercase tracking-[0.22em] text-ink-muted">{k}</span>
    <span className={`text-sm font-mono-accent ${danger ? "text-[#C94F4F]" : "text-artha-amber"}`}>{v}</span>
  </div>
);

export default PublicCompany;
